import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from "@effect/platform";
import * as S from "effect/Schema";
import { EmployeeCsvRow } from "@pima/contracts/messages";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { EmailTaken, InvalidUserInput } from "./session.js";
import { Forbidden } from "./shares.js";
import { PlainDateFromString } from "./temporal.js";
import { LocationRow } from "./admin-locations.js";
import { AccountEmailSchema, LanguageSchema } from "./column-schemas.js";
import { InviteUrlNotAllowed, LastAdminRemaining } from "./admin-users.js";
/** The employee directory's typed, domain-shaped read model. */
export const EmployeeRow = S.Struct({
    id: S.NullOr(S.UUID),
    user_id: S.NullOr(S.UUID),
    client_id: S.NullOr(S.UUID),
    client_name: S.NullOr(S.String),
    client_categories: S.NullOr(S.String),
    department_id: S.NullOr(S.UUID),
    department_name: S.NullOr(S.String),
    department_key: S.NullOr(S.String),
    department_description: S.NullOr(S.String),
    job_title_id: S.NullOr(S.UUID),
    job_title_name: S.NullOr(S.String),
    job_title_key: S.NullOr(S.String),
    job_title_description: S.NullOr(S.String),
    personal_email: S.NullOr(S.String),
    personal_number: S.NullOr(S.String),
    date_of_birth: S.NullOr(S.String),
    unique_key: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    user_email: S.NullOr(S.String),
    user_first_name: S.NullOr(S.String),
    user_last_name: S.NullOr(S.String),
    user_status: S.NullOr(S.String),
    user_language: S.NullOr(S.String),
    user_last_access: S.NullOr(S.String),
    user_role_id: S.NullOr(S.UUID),
    user_role_name: S.NullOr(S.String),
    user_public_registration: S.NullOr(S.Boolean),
    user_additional_roles: S.NullOr(S.String),
    location_ids: S.NullOr(S.String),
    location_names: S.NullOr(S.String),
    location_keys: S.NullOr(S.String),
    location_count: S.NullOr(S.String),
});
/**
 * A comma-joined id list on the wire, decoded to an array — the same
 * encoding `measures.exportMeasuresCsv`'s `type` filter uses, so a
 * location-manager's location scope rides one query param instead of
 * repeating `location_ids` per value.
 */
const UuidCsvList = S.transform(S.String, S.Array(S.UUID), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (ids) => ids.join(","),
});
/**
 * The employee-list "Sort by" control (PMO-19 #476/#523): alphabetical by
 * first/last name in either direction, or the table's original
 * insertion-order default. Omitted resolves to `"recent"`.
 */
export const EmployeeSort = S.Literal("name_asc", "name_desc", "recent");
export const EmployeeRoleKind = S.Literal("ohs-officer", "location-manager");
const EmployeeRoleKindCsvList = S.transform(S.String, S.Array(EmployeeRoleKind), {
    strict: true,
    decode: (value) => value
        .split(",")
        .map((entry) => entry.trim())
        .filter((entry) => entry === "ohs-officer" || entry === "location-manager"),
    encode: (values) => values.join(","),
});
export const ListEmployeesParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    search: S.optional(S.String),
    location_ids: S.optional(UuidCsvList),
    // Narrows the licenses view to allocated OHS roles, distinct from
    // `location_ids`, which narrows by assignment rather than role.
    role_ids: S.optional(UuidCsvList),
    role_kinds: S.optional(EmployeeRoleKindCsvList),
    sort: S.optional(EmployeeSort),
}));
export const ListEmployees = HttpApiEndpoint.get("listEmployees", "/employees")
    .setUrlParams(ListEmployeesParams)
    .addSuccess(pagedResponse(EmployeeRow))
    .addError(Forbidden, { status: 403 });
export const UserLocationAssignment = S.Struct({
    share_id: S.UUID,
    user_id: S.UUID,
    client_id: S.UUID,
    location: LocationRow,
});
export const ListUserLocationsParams = S.Struct({
    user_id: S.UUID,
    client_id: S.optional(S.UUID),
});
export const ListUserLocations = HttpApiEndpoint.get("listUserLocations", "/employees/user-locations")
    .setUrlParams(ListUserLocationsParams)
    .addSuccess(S.Struct({ items: S.Array(UserLocationAssignment) }))
    .addError(Forbidden, { status: 403 });
export const ListUsersByLocationsPayload = S.Struct({
    client_id: S.UUID,
    location_ids: S.Array(S.UUID),
});
export const ListUsersByLocations = HttpApiEndpoint.post("listUsersByLocations", "/employees/users-by-locations")
    .setPayload(ListUsersByLocationsPayload)
    .addSuccess(S.Struct({ user_ids: S.Array(S.UUID) }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// departments / job-titles / operational-areas — id-array reads, replacing
// the `_in`-filter-over-50-ids URL hack (`apps/web/src/lib/api/batch-utils.ts`)
// for these three collections.
// ---------------------------------------------------------------------------
export const DepartmentRow = S.Struct({
    id: S.UUID,
    name: S.String,
    key: S.NullOr(S.String),
    description: S.NullOr(S.String),
});
export const JobTitleRow = S.Struct({
    id: S.UUID,
    name: S.String,
    key: S.NullOr(S.String),
    description: S.NullOr(S.String),
});
export const FilterOperator = S.Literal("and", "or");
/**
 * `filter` is the create-group modal's own picker-selection cache (each
 * criterion's display objects, not just ids) so re-opening a group for
 * editing can restore the picker state without re-deriving it — the group's
 * membership itself is the separately-computed `user_ids`. Opaque on this
 * contract, same convention as every other jsonb `metadata` column here.
 */
export const OperationalAreaRow = S.Struct({
    id: S.UUID,
    name: S.String,
    user_ids: S.Array(S.UUID),
    filter_operator: FilterOperator,
    filter: S.NullOr(S.Unknown),
});
export const OperationalAreaNotFound = S.Struct({
    _tag: S.Literal("OperationalAreaNotFound"),
});
export const CreateOperationalAreaPayload = S.Struct({
    client_id: S.UUID,
    name: S.NonEmptyString,
    user_ids: S.Array(S.UUID),
    filter_operator: FilterOperator,
    filter: S.optional(S.NullOr(S.Unknown)),
});
export const CreateOperationalArea = HttpApiEndpoint.post("createOperationalArea", "/employees/operational-areas/create")
    .setPayload(CreateOperationalAreaPayload)
    .addSuccess(S.Struct({ data: OperationalAreaRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateOperationalAreaPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    name: S.optional(S.NonEmptyString),
    user_ids: S.optional(S.Array(S.UUID)),
    filter_operator: S.optional(FilterOperator),
    filter: S.optional(S.NullOr(S.Unknown)),
});
export const UpdateOperationalArea = HttpApiEndpoint.post("updateOperationalArea", "/employees/operational-areas/update")
    .setPayload(UpdateOperationalAreaPayload)
    .addSuccess(S.Struct({ data: OperationalAreaRow }))
    .addError(OperationalAreaNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteOperationalAreaPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
});
export const DeleteOperationalArea = HttpApiEndpoint.post("deleteOperationalArea", "/employees/operational-areas/delete")
    .setPayload(DeleteOperationalAreaPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(OperationalAreaNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/**
 * The create-group modal's AND/OR matching engine — computed server-side so
 * the browser consumes only a typed domain result. `employee_ids` are
 * direct picks: always unioned into the result regardless of
 * `filter_operator`, matching the modal's "explicit override" rule. `and`
 * mode intersects every other non-empty criterion (an employee
 * must satisfy every given department/job-title/role condition at once,
 * narrowed further to users assigned any given location); `or` mode unions
 * across each criterion independently.
 */
export const PreviewGroupMatchesPayload = S.Struct({
    client_id: S.UUID,
    filter_operator: FilterOperator,
    location_ids: S.optional(S.Array(S.UUID)),
    department_ids: S.optional(S.Array(S.UUID)),
    job_title_ids: S.optional(S.Array(S.UUID)),
    role_ids: S.optional(S.Array(S.UUID)),
    employee_ids: S.optional(S.Array(S.UUID)),
});
export const PreviewGroupMatches = HttpApiEndpoint.post("previewGroupMatches", "/employees/operational-areas/preview-matches")
    .setPayload(PreviewGroupMatchesPayload)
    .addSuccess(S.Struct({ user_ids: S.Array(S.UUID) }))
    .addError(Forbidden, { status: 403 });
/**
 * `ids` narrows to that subset when given; omitted, every row for
 * `client_id` comes back — the caller decides which shape it needs rather
 * than the contract offering two intents per collection.
 */
export const ListByClientPayload = S.Struct({
    client_id: S.UUID,
    ids: S.optional(S.Array(S.UUID)),
});
/**
 * Own-client-scoped locations read — the same `locations` table
 * `admin-locations.listLocations` exposes cross-client for the admin config
 * editor, here reachable by any caller who can act in the client (the
 * upload-employees CSV screen's location list, the precautionary-index
 * filter dropdown), not just an admin/OHS role.
 */
export const ListEmployeeLocations = HttpApiEndpoint.post("listEmployeeLocations", "/employees/locations")
    .setPayload(ListByClientPayload)
    .addSuccess(S.Struct({ items: S.Array(LocationRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const ListDepartments = HttpApiEndpoint.post("listDepartments", "/employees/departments")
    .setPayload(ListByClientPayload)
    .addSuccess(S.Struct({ items: S.Array(DepartmentRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const ListJobTitles = HttpApiEndpoint.post("listJobTitles", "/employees/job-titles")
    .setPayload(ListByClientPayload)
    .addSuccess(S.Struct({ items: S.Array(JobTitleRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const ListOperationalAreas = HttpApiEndpoint.post("listOperationalAreas", "/employees/operational-areas")
    .setPayload(ListByClientPayload)
    .addSuccess(S.Struct({ items: S.Array(OperationalAreaRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
/**
 * The upsert-employee modal's inline "create new" affordance on the
 * department/job-title pickers — a plain client-scoped insert, no lookup
 * table beyond uniqueness the DB itself enforces.
 */
export const CreateDepartmentPayload = S.Struct({
    client_id: S.UUID,
    name: S.NonEmptyString,
});
export const DepartmentTaken = S.Struct({
    _tag: S.Literal("DepartmentTaken"),
});
export const CreateDepartment = HttpApiEndpoint.post("createDepartment", "/employees/departments/create")
    .setPayload(CreateDepartmentPayload)
    .addSuccess(S.Struct({ data: DepartmentRow }))
    .addError(Forbidden, { status: 403 })
    .addError(DepartmentTaken, { status: 409 });
export const CreateJobTitlePayload = S.Struct({
    client_id: S.UUID,
    name: S.NonEmptyString,
});
export const JobTitleTaken = S.Struct({
    _tag: S.Literal("JobTitleTaken"),
});
export const CreateJobTitle = HttpApiEndpoint.post("createJobTitle", "/employees/job-titles/create")
    .setPayload(CreateJobTitlePayload)
    .addSuccess(S.Struct({ data: JobTitleRow }))
    .addError(Forbidden, { status: 403 })
    .addError(JobTitleTaken, { status: 409 });
// ---------------------------------------------------------------------------
// licenses — the seats-usage table (licenses-view) and the upsert-employee
// modal's own pre-flight check (`checkClientLicenses`) folded into one read:
// the client's two license limits plus how many ohs-officer/location-manager
// users currently occupy them.
// ---------------------------------------------------------------------------
export const ClientLicenses = S.Struct({
    ohsOfficerLimit: S.Int,
    locationManagerLimit: S.Int,
    ohsOfficerUsed: S.Int,
    locationManagerUsed: S.Int,
});
export const GetClientLicensesParams = S.Struct({
    client_id: S.UUID,
});
export const GetClientLicenses = HttpApiEndpoint.get("getClientLicenses", "/employees/licenses")
    .setUrlParams(GetClientLicensesParams)
    .addSuccess(S.Struct({ data: ClientLicenses }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// dummy-email — pure generation, no legacy service call.
// ---------------------------------------------------------------------------
export const GenerateDummyEmailPayload = S.Struct({
    first_name: S.NonEmptyString,
    domain: S.NonEmptyString,
});
export const GenerateDummyEmail = HttpApiEndpoint.post("generateDummyEmail", "/employees/dummy-email")
    .setPayload(GenerateDummyEmailPayload)
    .addSuccess(S.Struct({ data: S.Struct({ email: S.String }) }));
// ---------------------------------------------------------------------------
// upload/validate — pre-flight email check for a bulk-upload CSV, ahead of
// durable acceptance. Employee mutation runs in the event listener.
// ---------------------------------------------------------------------------
export const ValidateEmployeeUploadPayload = S.Struct({
    client_id: S.UUID,
    emails: S.Array(S.String),
});
export const ValidateEmployeeUploadResult = S.Struct({
    existing_emails: S.Array(S.String),
    new_emails: S.Array(S.String),
    conflicting_emails: S.Array(S.String),
    existing_count: S.Int,
    new_count: S.Int,
    conflicting_count: S.Int,
});
export const ValidateEmployeeUpload = HttpApiEndpoint.post("validateEmployeeUpload", "/employees/upload/validate")
    .setPayload(ValidateEmployeeUploadPayload)
    .addSuccess(S.Struct({ data: ValidateEmployeeUploadResult }))
    .addError(Forbidden, { status: 403 });
export const QueueEmployeeUploadPayload = S.Struct({
    client_id: S.UUID,
    users: S.Array(S.Struct({
        ...EmployeeCsvRow.fields,
        client_id: S.UUID,
        role: S.UUID,
        location_id: S.optional(S.UUID),
    })),
    mode: S.optional(S.Literal("upsert")),
    match_by: S.optional(S.Literal("email", "work_email")),
    overwrite: S.optional(S.Literal("full")),
});
export const InvalidEmployeeUpload = S.Struct({
    _tag: S.Literal("InvalidEmployeeUpload"),
    reason: S.String,
});
export const QueueEmployeeUpload = HttpApiEndpoint.post("queueEmployeeUpload", "/employees/upload/queue")
    .setPayload(QueueEmployeeUploadPayload)
    .addSuccess(S.Struct({ message: S.String, data: S.Struct({ upload_id: S.UUID }) }))
    .addError(InvalidEmployeeUpload, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const EmployeeCsvUploadStatus = HttpApiEndpoint.get("employeeCsvUploadStatus", "/employees/upload/:id/status")
    .setPath(S.Struct({ id: S.UUID }))
    .addSuccess(S.Struct({
    data: S.Struct({
        id: S.UUID,
        status: S.String,
        total_users: S.Int,
        processed_count: S.Int,
        failed_count: S.Int,
        error_message: S.NullOr(S.String),
        upload_timestamp: S.NullOr(S.String),
        processing_started_at: S.NullOr(S.String),
        processing_completed_at: S.NullOr(S.String),
        progress_percentage: S.Number,
    }),
}))
    .addError(InvalidEmployeeUpload, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const EmployeeUploadPath = S.Struct({ upload_id: S.UUID });
/**
 * The failed rows as a CSV download. A missing upload and one the caller
 * may not see answer the same 403.
 */
export const DownloadEmployeeUploadFailedRows = HttpApiEndpoint.get("downloadEmployeeUploadFailedRows", "/employees/upload/:upload_id/failed-rows.csv")
    .setPath(EmployeeUploadPath)
    .addSuccess(HttpApiSchema.Uint8Array({ contentType: "text/csv; charset=utf-8" }))
    .addError(Forbidden, { status: 403 });
/**
 * create / update — direct `yp_users` + `user_metadata` + `location_shares`
 * writes. Deliberately bypasses `UsersService`: that legacy service runs the
 * full Directus dynamic permission engine keyed on the caller's real role
 * grants, which this bridge never establishes (see `principal.ts` — the
 * bridge authorizes from `CurrentPrincipal`, not Directus permissions), so
 * reusing it here would silently 403 every caller this contract intends to
 * allow. Both writes stay within the caller's own client unless the caller
 * is an admin/ohs-admin — the same `canManageHris`-style rule `hris.*`
 * enforces. The handler applies two permission checks: `role` can't be an admin-access role for a
 * non-admin caller, and `department_id`/`job_title_id`/`location_ids` must
 * resolve inside the same client.
 */
export const CreateEmployeePayload = S.Struct({
    client_id: S.UUID,
    email: AccountEmailSchema,
    role: S.UUID,
    first_name: S.optional(S.String),
    last_name: S.optional(S.String),
    language: S.optional(LanguageSchema),
    department_id: S.optional(S.UUID),
    job_title_id: S.optional(S.UUID),
    personal_email: S.optional(S.String),
    personal_number: S.optional(S.String),
    date_of_birth: S.optional(PlainDateFromString),
    location_ids: S.optional(S.Array(S.UUID)),
    additional_roles: S.optional(S.String),
    email_notifications: S.optional(S.Boolean),
    /** The employee has no real address yet and registers through a link. */
    public_registration: S.optional(S.Boolean),
});
export const CreateEmployee = HttpApiEndpoint.post("createEmployee", "/employees")
    .setPayload(CreateEmployeePayload)
    .addSuccess(S.Struct({ data: EmployeeRow }))
    .addError(EmailTaken, { status: 409 })
    .addError(InvalidUserInput, { status: 400 })
    .addError(Forbidden, { status: 403 });
export const InviteEmployeePayload = S.Struct({
    client_id: S.UUID,
    user_id: S.optional(S.UUID),
    email: AccountEmailSchema,
    role: S.UUID,
    invite_url: S.optional(S.String),
    first_name: S.optional(S.String),
    last_name: S.optional(S.String),
    language: S.optional(LanguageSchema),
    additional_roles: S.optional(S.String),
    email_notifications: S.optional(S.Boolean),
    department_id: S.optional(S.UUID),
    job_title_id: S.optional(S.UUID),
    personal_email: S.optional(S.String),
    personal_number: S.optional(S.String),
    date_of_birth: S.optional(PlainDateFromString),
    location_ids: S.optional(S.Array(S.UUID)),
});
/**
 * Invites a member of the caller's client from the employee directory, or
 * re-sends the invitation to a draft, invited or unverified member. An active,
 * archived or suspended account, or one of another client, is left untouched
 * with `invited: false` and `user_id: null` for another client's account.
 */
export const InviteEmployee = HttpApiEndpoint.post("inviteEmployee", "/employees/invite")
    .setPayload(InviteEmployeePayload)
    .addSuccess(S.Struct({
    data: S.Struct({ user_id: S.NullOr(S.UUID), invited: S.Boolean }),
}))
    .addError(EmailTaken, { status: 409 })
    .addError(InvalidUserInput, { status: 400 })
    .addError(InviteUrlNotAllowed, { status: 422 })
    .addError(Forbidden, { status: 403 });
export const EmployeeNotFound = S.Struct({
    _tag: S.Literal("EmployeeNotFound"),
});
export const UpdateEmployeePayload = S.Struct({
    user_id: S.UUID,
    client_id: S.UUID,
    email: S.optional(AccountEmailSchema),
    role: S.optional(S.UUID),
    email_notifications: S.optional(S.Boolean),
    first_name: S.optional(S.String),
    last_name: S.optional(S.String),
    language: S.optional(LanguageSchema),
    status: S.optional(S.Literal("draft", "invited", "active", "suspended", "archived")),
    department_id: S.optional(S.NullOr(S.UUID)),
    job_title_id: S.optional(S.NullOr(S.UUID)),
    personal_email: S.optional(S.NullOr(S.String)),
    personal_number: S.optional(S.NullOr(S.String)),
    date_of_birth: S.optional(S.NullOr(PlainDateFromString)),
    location_ids: S.optional(S.Array(S.UUID)),
    additional_roles: S.optional(S.NullOr(S.String)),
});
export const UpdateEmployee = HttpApiEndpoint.post("updateEmployee", "/employees/update")
    .setPayload(UpdateEmployeePayload)
    .addSuccess(S.Struct({ data: EmployeeRow }))
    .addError(EmployeeNotFound, { status: 404 })
    .addError(EmailTaken, { status: 409 })
    .addError(Forbidden, { status: 403 });
export const DeleteEmployeePayload = S.Struct({ id: S.UUID });
/** A user the caller may not delete answers as `EmployeeNotFound`, like a missing one. */
export const DeleteEmployee = HttpApiEndpoint.post("deleteEmployee", "/employees/delete")
    .setPayload(DeleteEmployeePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(EmployeeNotFound, { status: 404 })
    .addError(LastAdminRemaining, { status: 422 });
export const ListAdditionalRolesPayload = S.Struct({
    client_id: S.UUID,
    search: S.String,
    limit: S.Int.pipe(S.between(1, 100)),
});
export const ListAdditionalRoles = HttpApiEndpoint.post("listAdditionalRoles", "/employees/additional-roles")
    .setPayload(ListAdditionalRolesPayload)
    .addSuccess(S.Struct({ data: S.Array(S.String) }))
    .addError(Forbidden, { status: 403 });
export const GetEmployeeInvitationLinkPayload = S.Struct({
    user_id: S.UUID,
    url: S.NullOr(S.String),
});
export const InvitationLinkInvalidUrl = S.Struct({
    _tag: S.Literal("InvitationLinkInvalidUrl"),
    reason: S.String,
});
export const InvitationLinkUnavailable = S.Struct({
    _tag: S.Literal("InvitationLinkUnavailable"),
    reason: S.String,
});
export const GetEmployeeInvitationLink = HttpApiEndpoint.post("getEmployeeInvitationLink", "/employees/invitation-link")
    .setPayload(GetEmployeeInvitationLinkPayload)
    .addSuccess(S.Struct({ url: S.String }))
    .addError(Forbidden, { status: 403 })
    .addError(InvitationLinkInvalidUrl, { status: 400 })
    .addError(InvitationLinkUnavailable, { status: 422 });
export const employeesGroup = HttpApiGroup.make("employees")
    .add(ListAdditionalRoles)
    .add(GetEmployeeInvitationLink)
    .add(ListEmployees)
    .add(ListUserLocations)
    .add(ListUsersByLocations)
    .add(ListEmployeeLocations)
    .add(ListDepartments)
    .add(ListJobTitles)
    .add(ListOperationalAreas)
    .add(CreateOperationalArea)
    .add(UpdateOperationalArea)
    .add(DeleteOperationalArea)
    .add(PreviewGroupMatches)
    .add(CreateDepartment)
    .add(CreateJobTitle)
    .add(GetClientLicenses)
    .add(GenerateDummyEmail)
    .add(ValidateEmployeeUpload)
    .add(QueueEmployeeUpload)
    .add(EmployeeCsvUploadStatus)
    .add(DownloadEmployeeUploadFailedRows)
    .add(CreateEmployee)
    .add(InviteEmployee)
    .add(UpdateEmployee)
    .add(DeleteEmployee)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=employees.js.map